import { cellToBoundary } from "h3-js";
import { http } from "./http";

// NOTE: endpoints are now relative to API_BASE
const KELP_API_PATH = "/tellus/map/kelp";
const KELP_DATES_API_PATH = "/tellus/map/kelp/dates";
const KELP_AGGREGATES_API_PATH = "/tellus/map/kelp/aggregates";

export type KelpViewMode = "snapshot" | "monthly" | "quarterly";

export type KelpAggregateCell = {
  h3: string;
  max_coverage: number;
  mean_coverage: number;
  scan_count: number;
  first_detected: string | null;
  last_detected: string | null;
};

export type KelpAggregatesResponse = {
  period: string;
  anchor_date: string;
  window_start: string;
  cell_count: number;
  cells: KelpAggregateCell[];
};

export async function fetchKelpAggregates(params: {
  period: "monthly" | "quarterly";
  anchor_date: string;
  bbox?: [number, number, number, number];
  zoom?: number;
  signal?: AbortSignal;
}): Promise<KelpAggregatesResponse> {
  const { signal, ...rest } = params;
  const p: Record<string, string | number> = {
    period: rest.period,
    anchor_date: rest.anchor_date,
  };
  if (Array.isArray(rest.bbox) && rest.bbox.length === 4) p.bbox = rest.bbox.join(",");
  if (typeof rest.zoom === "number" && Number.isFinite(rest.zoom)) p.zoom = Math.round(rest.zoom);
  const res = await http.get<KelpAggregatesResponse>(KELP_AGGREGATES_API_PATH, { params: p, signal });
  return res.data;
}

export type KelpDatesQuery = {
  bbox?: [number, number, number, number];
  zoom?: number;
};

export async function fetchAvailableKelpDates(query: KelpDatesQuery = {}): Promise<string[]> {
  const params: Record<string, string | number> = {};
  if (Array.isArray(query.bbox) && query.bbox.length === 4) {
    params.bbox = query.bbox.join(",");
  }
  if (typeof query.zoom === "number" && Number.isFinite(query.zoom)) {
    params.zoom = Math.round(query.zoom);
  }
  const res = await http.get(KELP_DATES_API_PATH, { params });
  return res.data.available_dates ?? [];
}

type KelpCellRow = {
  h3: string;
  coverage?: number | null;
  kelp_fraction?: number | null;
};

type KelpApiResponse =
  | { resolution: number; date?: string; features: any[]; query_ms?: number }
  | { resolution: number; date?: string; cells: KelpCellRow[]; query_ms?: number };

export function cellsToGeojsonFeatures(cells: KelpCellRow[]) {
  return (cells || []).map((c) => {
    const h3Id = c.h3;
    const cov =
      c.coverage != null ? c.coverage : c.kelp_fraction != null ? c.kelp_fraction : null;

    const ring = cellToBoundary(h3Id, true);
    const closed =
      ring.length > 0 &&
      (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])
        ? [...ring, ring[0]]
        : ring;

    return {
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [closed] },
      properties: { h3: h3Id, h3_id: h3Id, coverage: cov },
    };
  });
}

export async function fetchKelpGeojson(params: {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
  zoom: number;
  date: string;
  signal?: AbortSignal;
}): Promise<{ resolution: number; date?: string; features: any[]; queryMs?: number; cellCount?: number }> {
  const { signal, ...queryParams } = params;
  const res = await http.get<KelpApiResponse>(KELP_API_PATH, { params: queryParams, signal });
  const data: any = res.data || {};

  const resolution = Number(data.resolution ?? 0);
  const date = typeof data.date === "string" ? data.date : undefined;
  const queryMs = typeof data.query_ms === "number" ? data.query_ms : undefined;

  if (Array.isArray(data.cells)) {
    const features = cellsToGeojsonFeatures(data.cells);
    return { resolution, date, features, queryMs, cellCount: data.cells.length };
  }

  if (Array.isArray(data.features)) {
    const normalizedFeatures = data.features.map((feature: any) => {
      const props = feature?.properties || {};
      return {
        ...feature,
        properties: {
          ...props,
          h3: props.h3 ?? props.h3_id ?? "—",
          coverage: props.coverage ?? props.kelp_fraction ?? null,
        },
      };
    });
    return { resolution, date, features: normalizedFeatures, queryMs, cellCount: normalizedFeatures.length };
  }

  return { resolution, date, features: [], queryMs, cellCount: 0 };
}
